import { useState, useEffect } from "react";

import {
  useGetModeratorById,
  usePauseModeratorAccount,
} from "@/entities/moderator";
import { useSessionStore } from "@/entities/session";
import { Roles } from "@/entities/user";
import { Button } from "@/shared/ui/button";
import { Spinner } from "@/shared/ui/spinner";
import { type Moderator } from "@/entities/moderator/model";
import type { MentorProfile } from "@/entities/mentors-profile";
import {
  MentorProfileStatus,
  useMentorProfile,
  useToggleMentorProfile,
} from "@/entities/mentors-profile";

export const PauseButton = () => {
  const { user } = useSessionStore();

  const getQueryConfig = () => {
    if (!user?.role) return null;
    switch (user.role) {
      case Roles.MasterModerator:
      case Roles.Moderator:
        return useGetModeratorById;
      case Roles.MasterMentor:
      case Roles.Mentor:
        return useMentorProfile;
      default:
        return null;
    }
  };

  const queryConfig = getQueryConfig();
  const { data, isLoading } = queryConfig
    ? queryConfig({ userId: user?._id! })
    : { data: null };

  const [pauseStatus, setPauseStatus] = useState<boolean | null>(null);

  useEffect(() => {
    const getPauseStatus = () => {
      if (!data?.data) return null;
      switch (user?.role) {
        case Roles.MasterMentor:
        case Roles.Mentor:
          return !(
            (data.data as MentorProfile).status === MentorProfileStatus.ACTIVE
          );
        case Roles.MasterModerator:
        case Roles.Moderator:
          return (data.data as Moderator).pauseStatus.self;
        default:
          return null;
      }
    };

    setPauseStatus(getPauseStatus());
  }, [data?.data, user?.role]);

  const { mutateAsync: pauseMentorAccount, isPending } =
    useToggleMentorProfile();
  const { mutateAsync: pauseModeratorAccount, isPending: isPendingModerator } =
    usePauseModeratorAccount();

  const handleClick = async () => {
    if (pauseStatus === null) return;

    switch (user?.role) {
      case Roles.Mentor:
      case Roles.MasterMentor:
        await pauseMentorAccount({
          id: (data?.data as MentorProfile).id!,
          status: pauseStatus
            ? MentorProfileStatus.ACTIVE
            : MentorProfileStatus.PAUSED_BY_MENTOR,
        });
        break;
      case Roles.Moderator:
      case Roles.MasterModerator:
        await pauseModeratorAccount({ isActive: !pauseStatus });
    }

    setPauseStatus((prev) => !prev);
  };

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      disabled={
        isPending ||
        isPendingModerator ||
        isLoading ||
        (user?.role === Roles.Moderator &&
          (data?.data as Moderator).pauseStatus.head) ||
        ([Roles.Mentor, Roles.MasterMentor].includes(user?.role!) && !data)
      }
    >
      <p className="text-textColor">
        {isPending || isPendingModerator || isLoading ? (
          <Spinner className="text-black dark:text-white" />
        ) : pauseStatus ? (
          "Возобновить"
        ) : (
          "Пауза"
        )}
      </p>
    </Button>
  );
};
